<script setup lang="ts">
import { computed } from 'vue'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import markedKatex from 'marked-katex-extension'
import 'katex/dist/katex.min.css'
import CitationList from './CitationList.vue'
import MessageFeedback from './MessageFeedback.vue'
import type { ChatMessage } from '../types'

marked.use(
  markedKatex({
    throwOnError: false,
    nonStandard: true,
  }),
)

const props = defineProps<{
  message: ChatMessage
  feedbackDisabled?: boolean
}>()

const emit = defineEmits<{
  feedback: [messageId: string, reaction: '' | 'like' | 'dislike', comment: string]
}>()

function normalizeMathMarkdown(content: string): string {
  return content.replace(
    /(^|\n)[ \t]*\$\$[ \t]*\n([\s\S]*?)\n[ \t]*\$\$[ \t]*(?=\n|$)/g,
    (_match, start: string, formula: string) =>
      `${start}\n$$\n${formula.trim()}\n$$\n`,
  )
}

const safeHtml = computed(() =>
  DOMPurify.sanitize(
    marked.parse(normalizeMathMarkdown(props.message.content || '正在思考…'), {
      breaks: true,
      gfm: true,
    }) as string,
  ),
)
</script>

<template>
  <article
    class="message"
    :class="[
      message.role,
      { streaming: message.streaming, comparison: message.public_label },
    ]"
  >
    <header v-if="message.role === 'assistant'" class="message-header">
      <span class="professor-mark" aria-label="beta hat">
        <span class="beta-symbol" aria-hidden="true">β</span>
      </span>
      <span>{{
        message.public_label ? `回答 ${message.public_label}` : '计量客栈'
      }}</span>
      <span v-if="message.retrieval_mode === 'rag'" class="source-mode">
        教材增强
      </span>
      <span v-else-if="message.retrieval_mode === 'pure_llm'" class="source-mode outside">
        AI 拓展
      </span>
    </header>
    <div v-else class="student-label">你的问题</div>
    <div class="message-content" v-html="safeHtml"></div>
    <p v-if="message.status === 'failed'" class="message-incomplete">此回答未完成，不能作为有效评价回答。</p>
    <span v-if="message.streaming" class="typing-caret" aria-label="正在生成"></span>
    <CitationList
      v-if="message.role === 'assistant'"
      :citations="message.citations || []"
      compact
    />
    <MessageFeedback
      v-if="
        message.role === 'assistant' &&
        !message.public_label &&
        !message.streaming
      "
      :reaction="message.feedback_reaction || ''"
      :comment="message.feedback_comment || ''"
      :submitted-at="message.feedback_submitted_at"
      :disabled="feedbackDisabled"
      @submit="
        (reaction, comment) => emit('feedback', message.id, reaction, comment)
      "
    />
  </article>
</template>
